import {
  safeAlert, reportResult, rangeDetail,
  RunDetail, SRS_NAV_PREFIX, MAX_NAV_ITEMS,
  applydefaultparagraphformat
} from '../runs/common';
import { ensureRunStyles } from '../taskpane/styleEngine';
import { getConfig } from '../taskpane/configService';


const BM_PREFIX = SRS_NAV_PREFIX + "R3_";


interface TableInfo {
  tableIndex:             number;
  captionInTable:         boolean;
  captionOutside:         boolean;
  headerApplied:          boolean;
  headerRowIndex:         number;
  desiredHeaderRowCount:  number;
}

/** Shared state threaded through all phase helpers. */
interface RunState {
  navDetails:  RunDetail[];
  navCounter:  { count: number };
}

/** Config bundle for caption/header styles. */
interface StyleConfig {
  captionStyle:          string;
  headerStyle:           string;
  captionRegex:          RegExp;
  captionMustNotEndWith: string;
}


// ═══════════════════════════════════════════════════════════════════════════
// BOOKMARK HELPER
// ═══════════════════════════════════════════════════════════════════════════

function insertNavBookmark(
  range:   Word.Range,
  text:    string,
  note:    string,
  state:   RunState
): void {
  if (state.navCounter.count >= MAX_NAV_ITEMS) return;
  const bmName = BM_PREFIX + String(state.navCounter.count).padStart(3, "0");
  range.insertBookmark(bmName);
  state.navDetails.push(rangeDetail(bmName, text, note));
  state.navCounter.count++;
}


// ═══════════════════════════════════════════════════════════════════════════
// PHASE 1 HELPERS  (API-based detection + style application)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Apply caption style to paragraphs in the first row.
 */
async function styleCaptionInFirstRow(
  paras:        Word.ParagraphCollection,
  context:      Word.RequestContext,
  captionStyle: string,
  state:        RunState
): Promise<number> {
  const toStyle = paras.items.filter(p => p.style !== captionStyle);
  if (toStyle.length === 0) return 0;

  for (const para of toStyle) {
    para.style = captionStyle;
    para.load("text");
  }
  await context.sync(); // batched write + read

  for (const para of toStyle) {
    await applydefaultparagraphformat(para, captionStyle, context);
    const commentRange = para.getRange(Word.RangeLocation.whole);
    insertNavBookmark(commentRange, para.text, `\u2192 ${captionStyle} (internal)`, state);
  }
  await context.sync(); // flush bookmarks

  return toStyle.length;
}

// ─────────────────────────────────────────────────────────────────────────
// findExternalCaption helpers  (extracted to fix S3776 complexity 17→≤15)
// ─────────────────────────────────────────────────────────────────────────

/** Returns true when the paragraph's style signals a structural boundary. */
function isStructuralBoundary(para: Word.Paragraph): boolean {
  const style = (para.style || "").toLowerCase();
  return (
    (para.styleBuiltIn || "").toLowerCase().includes("heading") ||
    style.includes("heading") ||
    style.includes("toc") ||
    style.includes("hyperlink") ||
    style.includes("table of")
  );
}

/**
 * Applies caption style + comment + bookmark to a confirmed external caption.
 *
 * OPTIMIZED: previously did write-style/sync, then getRange/load/sync (result
 * unused), then write-same-style-again/sync, then comment+bookmark/sync — 4
 * round-trips. The mid-chain range load/rewrite was pure duplication of the
 * paragraph-level write above it. Now: one sync covers the style write (if
 * needed) plus the comment and bookmark insertion.
 */
async function styleExternalCaption(
  para:         Word.Paragraph,
  paraText:     string,
  captionStyle: string,
  context:      Word.RequestContext,
  state:        RunState
): Promise<void> {
  if (para.style !== captionStyle) {
    para.style = captionStyle;
    console.log(`  External caption styled: "${paraText.substring(0, 50)}"`);
  }

  const commentRange = para.getRange(Word.RangeLocation.whole);
  commentRange.insertComment(
    `${captionStyle} is not a part of table\n(Auto-detected and styled)`
  );
  insertNavBookmark(commentRange, paraText, `\u2192 ${captionStyle} (external)`, state);

  await context.sync(); // style write + comment + bookmark, batched

  await applydefaultparagraphformat(para, captionStyle, context);
}

/**
 * Search backward from the table for an external caption paragraph.
 * Applies style + comment + bookmark (all safe — outside the table).
 *
 * FIXED: prevPara can be a genuine null-object (e.g. table is the first
 * thing in the document body, so there's no paragraph before it). Chaining
 * a navigation property (parentTableOrNullObject) off a possibly-null-object
 * BEFORE confirming it exists risks the batch throwing at sync() instead of
 * resolving isNullObject gracefully. So the existence check must be isolated
 * in its own sync, and only once confirmed non-null is it safe to batch the
 * remaining property loads + the parent-table chain together.
 */
async function findExternalCaption(
  prevPara:              Word.Paragraph,
  context:               Word.RequestContext,
  captionStyle:          string,
  captionRegex:          RegExp,
  captionMustNotEndWith: string,
  state:                 RunState
): Promise<boolean> {
  prevPara.load("isNullObject");
  await context.sync(); // #1 — must confirm existence before any chained navigation
  if (prevPara.isNullObject) return false;

  // Safe to batch now — prevPara is confirmed to exist.
  prevPara.load("text, styleBuiltIn, style, uniqueLocalId");
  const parentTable = prevPara.parentTableOrNullObject;
  parentTable.load("isNullObject");
  await context.sync(); // #2

  if (!parentTable.isNullObject) {
    console.log(`  External caption search: walked into a table — stopping.`);
    return false;
  }

  if (isStructuralBoundary(prevPara)) return false;

  const paraText  = (prevPara.text || "").trim();
  const isCaption = captionRegex.test(paraText) && !paraText.endsWith(captionMustNotEndWith);

  if (!isCaption) {
    console.log(`  External caption search: hit non-caption content "${paraText.substring(0, 40)}" — stopping.`);
    return false;
  }

  await styleExternalCaption(prevPara, paraText, captionStyle, context, state);
  return true;
}

// ─────────────────────────────────────────────────────────────────────────
// applyHeaderStyle helpers  (extracted to fix S3776 complexity 19→≤15)
// ─────────────────────────────────────────────────────────────────────────

/** Ensures the named style exists in the document, creating it if needed. */
async function ensureStyleExists(
  styleName: string,
  context:   Word.RequestContext
): Promise<void> {
  const styleCheck = context.document.getStyles().getByNameOrNullObject(styleName);
  styleCheck.load("isNullObject");
  await context.sync();
  if (styleCheck.isNullObject) {
    context.document.addStyle(styleName, Word.StyleType.paragraph);
    await context.sync();
  }
}

/**
 * PASS 1: majority vote across non-empty paragraphs.
 * Returns { headerLike, normal } counts.
 *
 * OPTIMIZED: previously loaded each cell's paragraph collection in its own
 * sync, then loaded each paragraph's properties in its own sync — O(cells +
 * paragraphs) round-trips. Now: one sync loads every cell's paragraph
 * collection, one sync loads every paragraph's properties — 2 round-trips
 * total regardless of row size. Safe because `row.cells` is already loaded
 * by the caller (applyHeaderStyle) and a row's cells always exist once the
 * row itself exists — no null-object risk here.
 */
async function voteHeaderRow(
  row:         Word.TableRow,
  headerStyle: string,
  context:     Word.RequestContext
): Promise<{ headerLike: number; normal: number }> {
  const cellParaCollections = row.cells.items.map(cell => cell.body.paragraphs);
  for (const paras of cellParaCollections) paras.load("items");
  await context.sync(); // #1 — all cells' paragraph collections at once

  const allParas = cellParaCollections.flatMap(p => p.items);
  for (const para of allParas) {
    para.load("style, styleBuiltIn, font, text");
  }
  await context.sync(); // #2 — all paragraph properties at once

  let headerLike = 0;
  let normal     = 0;

  for (const para of allParas) {
    if (!(para.text || "").trim()) continue;
    const isHeader = (para.style || "").startsWith(headerStyle)
                  || (para.styleBuiltIn || "").startsWith(headerStyle)
                  || para.font.bold === true;
    if (isHeader) headerLike++; else normal++;
  }
  return { headerLike, normal };
}

/**
 * PASS 2: apply headerStyle to all paras in the row (majority already confirmed).
 * Returns true if any paragraph existed in the row (preserves original semantics).
 *
 * OPTIMIZED: previously, per paragraph: load+sync, then (if restyling) write
 * style+sync, getRange+load+sync (result unused), write same style again+sync
 * — up to 4 round-trips per paragraph, 2 of which were pure duplication. Now:
 * one sync loads every paragraph's properties, one sync writes style to every
 * paragraph that needs it.
 */
async function applyHeaderStyleToRow(
  row:         Word.TableRow,
  headerStyle: string,
  context:     Word.RequestContext,
  state:       RunState
): Promise<boolean> {
  const cellParaCollections = row.cells.items.map(cell => cell.body.paragraphs);
  for (const paras of cellParaCollections) paras.load("items");
  await context.sync(); // #1

  const allParas = cellParaCollections.flatMap(p => p.items);
  for (const para of allParas) {
    para.load("style, styleBuiltIn, text, uniqueLocalId");
  }
  await context.sync(); // #2

  const toStyle = allParas.filter(para => {
    const alreadyHeader = (para.style || "").startsWith(headerStyle)
                       || (para.styleBuiltIn || "").startsWith(headerStyle);
    return !alreadyHeader;
  });

  for (const para of toStyle) {
    para.style = headerStyle;
  }
  if (toStyle.length > 0) await context.sync(); // #3 — one batched write

  for (const para of toStyle) {
    await applydefaultparagraphformat(para, headerStyle, context);
    const range = para.getRange(Word.RangeLocation.whole);
    insertNavBookmark(range, para.text, `\u2192 ${headerStyle}`, state);
  }

  return allParas.length > 0;
}

/**
 * Apply header style to paragraphs in the target row.
 * Returns true if ANY paragraph was detected/styled as header.
 */
async function applyHeaderStyle(
  targetRow:   Word.TableRow,
  tableIndex:  number,
  context:     Word.RequestContext,
  headerStyle: string,
  state:       RunState
): Promise<boolean> {
  targetRow.cells.load("items");
  await context.sync();

  await ensureStyleExists(headerStyle, context);

  const { headerLike, normal } = await voteHeaderRow(targetRow, headerStyle, context);
  console.log(`  Table ${tableIndex + 1}: headerLike=${headerLike}, normal=${normal}`);

  if (headerLike <= normal) {
    console.log(`  Table ${tableIndex + 1}: not a header row — skipping`);
    return false;
  }

  return applyHeaderStyleToRow(targetRow, headerStyle, context, state);
}


// ═══════════════════════════════════════════════════════════════════════════
// PHASE 2: REPEAT HEADER CHECK VIA OOXML (CHECK ONLY — NO APPLY)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * CHECK ONLY — does NOT apply repeat header.
 * Reads OOXML to count existing <w:tblHeader/> rows.
 * If fewer than required, inserts a comment + Go To bookmark on the first row.
 * Returns: "ok" | "missing" | "failed"
 */
async function checkRepeatHeaderOoxml(
  table:          Word.Table,
  headerRowCount: number,
  tableIndex:     number,
  context:        Word.RequestContext,
  state:          RunState
): Promise<"ok" | "missing" | "failed"> {
  try {
    const tableRange  = table.getRange(Word.RangeLocation.whole);
    const ooxmlResult = tableRange.getOoxml();
    await context.sync();

    const originalXml  = ooxmlResult.value;
    const existingCount = (originalXml.match(/<w:tblHeader[\s/]/g) || []).length;

    if (existingCount >= headerRowCount) {
      console.log(`  Table ${tableIndex + 1}: repeat header OK (${existingCount} row(s) already set)`);
      return "ok";
    }

    console.log(`  Table ${tableIndex + 1}: repeat header MISSING (found ${existingCount}, expected ${headerRowCount})`);

    const firstRow = table.rows.getFirst();
    firstRow.cells.load("items");
    await context.sync();

    if (firstRow.cells.items.length === 0) return "missing";

    const para = firstRow.cells.items[0].body.paragraphs.getFirst();
    para.load("text");
    await context.sync();

    const range = para.getRange(Word.RangeLocation.whole);
    range.insertComment(
      `Repeat header not set.\nExpected ${headerRowCount} row(s) to repeat across pages.(Not auto-fixed — please set manually)`
    );
    insertNavBookmark(
      range,
      `Table ${tableIndex + 1}`,
      `Repeat header missing (expected ${headerRowCount} row${headerRowCount > 1 ? "s" : ""})`,
      state
    );
    await context.sync();
    return "missing";

  } catch (e) {
    console.warn(`  Table ${tableIndex + 1}: repeat header check failed: ${(e as Error).message}`);
    return "failed";
  }
}


// ═══════════════════════════════════════════════════════════════════════════
// PHASE 1 — per-table processor  (extracted from run3 to reduce S3776 31→≤15)
// ═══════════════════════════════════════════════════════════════════════════

/** Computes how many rows should repeat as headers for one table. */
function calcDesiredHeaderRowCount(captionInTable: boolean, headerApplied: boolean): number {
  if (captionInTable && headerApplied) return 2;
  if (captionInTable || headerApplied) return 1;
  return 0;
}

/**
 * Reads the first-row text of a table.
 * All paragraphs in the first cell are concatenated.
 *
 * OPTIMIZED: previously loaded the row's cells collection, then loaded the
 * first cell's `.body` (a navigation property — never actually needed a
 * load/sync), then loaded the paragraph collection, then loaded each
 * paragraph's text individually — up to 3 + N round-trips. `table.rows.getFirst()`
 * is safe (a table always has ≥1 row), so that part needs no round-trip.
 * `TableCellCollection` has no equivalent no-round-trip accessor, so reaching
 * the first cell still costs one sync. Paragraph properties are now batched
 * into a single sync instead of one sync per paragraph.
 */
async function readFirstRowText(
  table:   Word.Table,
  context: Word.RequestContext
): Promise<{ paras: Word.ParagraphCollection; text: string }> {
  const firstRow = table.rows.getFirst();
  firstRow.cells.load("items");
  await context.sync(); // #1 — reach the first cell

  const firstCell = firstRow.cells.items[0];
  const paras = firstCell.body.paragraphs;
  paras.load("items");
  await context.sync(); // #2 — load the paragraph collection

  for (const para of paras.items) {
    para.load("text, style");
  }
  await context.sync(); // #3 — load every paragraph's text/style at once

  let text = "";
  for (const para of paras.items) {
    text += (para.text || "").trim() + " ";
  }
  return { paras, text: text.trim() };
}

/** Processes one table through Phase 1 (caption + header detection/styling). */
async function processTablePhase1(
  table:      Word.Table,
  tableIndex: number,
  context:    Word.RequestContext,
  cfg:        StyleConfig,
  state:      RunState
): Promise<TableInfo | null> {
  table.load("rowCount, rows");
  table.rows.load("items");
  await context.sync();

  console.log(`\nTable ${tableIndex + 1} (${table.rows.items.length} rows):`);

  const { paras, text: firstRowText } = await readFirstRowText(table, context);
  const { captionStyle, headerStyle, captionRegex, captionMustNotEndWith } = cfg;

  let captionInTable = false;
  let captionOutside = false;

  // ── Caption detection ──
  if (captionRegex.test(firstRowText) && !firstRowText.endsWith(captionMustNotEndWith)) {
    captionInTable = true;
    await styleCaptionInFirstRow(paras, context, captionStyle, state);
    console.log(`  Caption IN table: "${firstRowText.substring(0, 60)}" --> table no ${tableIndex + 1}`);
  } else {
    const prevPara = table.getParagraphBeforeOrNullObject();
    captionOutside = await findExternalCaption(
      prevPara, context, captionStyle, captionRegex, captionMustNotEndWith, state
    );
    if (captionOutside) {
      console.log(`  Caption OUTSIDE table "${firstRowText.substring(0, 60)}" --> table no ${tableIndex + 1}`);
    } else {
      console.log(`  No caption found — skipping table`);
      return null;
    }
  }

  // ── Header row ──
  const headerRowIndex = captionInTable ? 1 : 0;

  if (table.rows.items.length <= headerRowIndex) {
    console.log(`  Skipped — no row at index ${headerRowIndex}`);
    return { tableIndex, captionInTable, captionOutside, headerApplied: false, headerRowIndex, desiredHeaderRowCount: 0 };
  }

  const headerApplied = await applyHeaderStyle(
    table.rows.items[headerRowIndex], tableIndex, context, headerStyle, state
  );

  const desiredHeaderRowCount = calcDesiredHeaderRowCount(captionInTable, headerApplied);

  console.log(`  headerRow=${headerRowIndex}, headerApplied=${headerApplied}, repeatRows=${desiredHeaderRowCount}`);

  return { tableIndex, captionInTable, captionOutside, headerApplied, headerRowIndex, desiredHeaderRowCount };
}


// ═══════════════════════════════════════════════════════════════════════════
// ENTRY POINT
// ═══════════════════════════════════════════════════════════════════════════

/** Full run logic — extracted so run3() itself has zero branches (S3776). */
async function runImpl(context: Word.RequestContext): Promise<void> {
  await ensureRunStyles(context, 3);
  context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

  const raw = getConfig().run3_tableCaption;
  const cfg: StyleConfig = {
    captionStyle:          raw.captionStyle,
    headerStyle:           raw.headerStyle,
    captionRegex:          new RegExp(raw.captionRegex, "i"),
    captionMustNotEndWith: raw.captionMustNotEndWith,
  };

  const tables = context.document.body.tables;
  tables.load("items");
  await context.sync();

  if (tables.items.length === 0) {
    context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
    await context.sync();
    reportResult('Table Caption & Header \u2014 no tables found', [
      { label: 'No tables in document', note: 'Nothing to process' },
    ]);
    return;
  }

  console.log("=".repeat(70));
  console.log("=== TABLE CAPTION & HEADER (3-PHASE) ===");
  console.log("=".repeat(70));
  console.log(`Tables found: ${tables.items.length}\n`);

  const state: RunState = { navDetails: [], navCounter: { count: 0 } };
  const tableInfos: TableInfo[] = [];
  let captionCount = 0;
  let headerCount  = 0;

  // ══════════════════════════════════════════════════════════════
  // PHASE 1: Detection + style application (API)
  // ══════════════════════════════════════════════════════════════

  console.log("PHASE 1: Caption detection + header styling");
  console.log("-".repeat(70));

  for (let i = 0; i < tables.items.length; i++) {
    const info = await processTablePhase1(tables.items[i], i, context, cfg, state);
    if (info === null) {
      tableInfos.push({
        tableIndex: i, captionInTable: false, captionOutside: false,
        headerApplied: false, headerRowIndex: 0, desiredHeaderRowCount: 0,
      });
      continue;
    }
    if (info.captionInTable || info.captionOutside) captionCount++;
    if (info.headerApplied) headerCount++;
    tableInfos.push(info);
  }

  await context.sync();

  context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
  await context.sync();

  // ══════════════════════════════════════════════════════════════
  // PHASE 2: Repeat header CHECK via OOXML (no apply)
  // ══════════════════════════════════════════════════════════════

  console.log("\n" + "-".repeat(70));
  console.log("PHASE 2: Repeat header CHECK via OOXML (check only, no fix)");
  console.log("-".repeat(70));

  const tables2 = context.document.body.tables;
  tables2.load("items");
  await context.sync();

  let missingRepeatCount = 0;

  for (const info of tableInfos) {
    if (info.desiredHeaderRowCount <= 0)         continue;
    if (info.tableIndex >= tables2.items.length) continue;

    const result = await checkRepeatHeaderOoxml(
      tables2.items[info.tableIndex],
      info.desiredHeaderRowCount,
      info.tableIndex,
      context,
      state
    );
    if (result === "missing") missingRepeatCount++;
  }

  console.log("\n" + "=".repeat(70));
  console.log(`Captions styled:               ${captionCount}`);
  console.log(`Header rows styled:            ${headerCount}`);
  console.log(`Repeat headers missing:        ${missingRepeatCount}`);
  console.log(`Go To bookmarks:               ${state.navCounter.count}`);
  console.log("\u2713 Table Caption & Header complete!");
  console.log("=".repeat(70));

  reportResult('Table Caption & Header Complete', [
    { label: 'Tables processed',                 count: tables.items.length },
    { label: 'Repeat headers missing (flagged)', count: missingRepeatCount },
    ...state.navDetails,
  ]);
}

export async function run3() {
  await Word.run(async (context) => {
    try {
      await runImpl(context);
    } catch (error) {
      console.error("Error in run3:", error);
      console.error("Message:", error.message);
      safeAlert("An error occurred during Table Caption & Header check.");
    }
  });
}